import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

// 3rd party imports
import { Subscription } from 'rxjs';

// devextreme imports
import notify from 'devextreme/ui/notify';

// cc-common imports
import { IconConstants, Utils } from 'cc-common';

// service imports
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { APAdjustmentService } from '../../_services/accounts-payable/ap-adjustment.service';
import { PrecisionService } from '../../../../_services/shared-services/precision.service';
import { DetailPopupsService } from '../../_services/detail-popups.service';
import { HelperService } from '../../../../_services/shared-services/helper.service';
import { APCreditApplicationsService } from '../../_services/accounts-payable/ap-credit-applications.service';
import { UserService } from '../../../../_services/data-services/user.service';

// model imports
import { APAdjustmentParameterDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-parameter-dto';
import { APAdjustmentResultDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-result-dto';
import { TransactionLogResultDto } from '../../_models/common/transaction-log-result-dto';
import { TransactionLogParameterDto } from '../../_models/common/transaction-log-parameter-dto';
import { APAdjustmentReverseParameterDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-reverse-parameter-dto';
import { APCreditApplicationParameterDto } from '../../_models/accounts-payable/ap-credit-application/ap-credit-application-parameter-dto';
import { APCreditApplicationResultDto } from '../../_models/accounts-payable/ap-credit-application/ap-credit-application-result-dto';
import { DimensionOptionCodeRawDto } from '../../_models/common/dimensions/dimension-option-code-raw-dto';
import { ReverseEditorResultDto } from '../../../../../../src/app/shared-module/_models/reverse-editor-result-dto';

// constant import
import { Constants } from '../../../../_constants/constants';
import { StringFormatConstants } from '../../../../_constants/string-format-constants';
import { MessageInterval } from '../../../../_constants/messages-intervals';
import { ARConstants } from '../../../cirabooks-module/_constants/cb-ar-constants';

// component imports
import { APAdjustmentEditorComponent } from '../ap-adjustment-editor/ap-adjustment-editor.component';

@Component({
    selector: 'app-ap-adjustment',
    templateUrl: './ap-adjustment.component.html',
    styleUrls: ['./ap-adjustment.component.scss']
})
export class APAdjustmentComponent implements OnInit, OnDestroy {
    @ViewChild('apAdjustmentEditor', { static: false }) apAdjustmentEditor: APAdjustmentEditorComponent;

    @Input() visible: boolean;
    @Input() adjustmentDetailParam: APAdjustmentParameterDto;

    @Output() onAdjustmentDetailClose = new EventEmitter<boolean>();

    popUpTitle: string;
    loadingVisible: boolean;
    adjustmentDetail: APAdjustmentResultDto;
    currencyFormat: any;
    dateFormat: any;
    transactionLogResultData: TransactionLogResultDto[] = [];

    displayTransactionLogPopUp = false;
    transactionLogTitle: string;
    communityId: number = null;
    attachmentData: any;
    displayAttachementPopUp = false;
    defaultLabelLocation: string;
    hasOtherDimensions: boolean;
    historyButtonOptions: any;

    showEditAdjustment = false;
    showReverseAdjustment = false;
    reverseEditorData = new ReverseEditorResultDto();
    reverseAPTitle: string;

    showApplyDebitEditor = false;
    isLimitedEdit = false;

    iconClose = IconConstants.CLOSE;
    iconTrash = IconConstants.DELETE;
    iconEdit = IconConstants.EDIT;
    iconCheck = IconConstants.CHECK;
    iconReverse = IconConstants.REVERSE;
    iconHistory = IconConstants.HISTORY;

    creditApplicationData: Array<APCreditApplicationResultDto> = [];
    creditApplicationTitle: string;
    showCreditApplication: boolean;

    transactionType = 'A';
    isInternalUser = false;

    forceReloadSubscription: Subscription;

    showRequiredDimensionSelector = false;
    requiedDimensionOptionCodes: Array<DimensionOptionCodeRawDto> = [];

    constructor(
        private readonly apAdjustmentService: APAdjustmentService,
        private readonly serverErrorService: ServerErrorService,
        private readonly precisionService: PrecisionService,
        private readonly detailPopupsService: DetailPopupsService,
        private readonly helperService: HelperService,
        private readonly apCreditApplicationService: APCreditApplicationsService,
        private readonly userService: UserService
    ) {
        this.isInternalUser = this.userService.isInternal();
        this.currencyFormat = this.precisionService.updatePrecisionPoint(StringFormatConstants.currencyFormat);
        this.onTransactionLogClick = this.onTransactionLogClick.bind(this);
        this.calculateCustomSummary = this.calculateCustomSummary.bind(this);
    }

    ngOnInit() {
        this.historyButtonOptions = {
            icon: this.iconHistory,
            hint: 'Transaction History',
            onClick: this.onTransactionLogClick
        };

        this.dateFormat = StringFormatConstants.dateFormat;
        this.defaultLabelLocation = Constants.defaultFormLabelLocation;
        if (Utils.notNullAndDefined(this.adjustmentDetailParam)) {
            this.popUpTitle = 'AP Adjustment ' + this.adjustmentDetailParam.id;
            this.populateAPAdjustmentDetail(true);
        }

        this.forceReloadSubscription = this.detailPopupsService.onForceReload().subscribe(() => {
            if (Utils.notNullAndDefined(this.adjustmentDetailParam)) {
                this.populateAPAdjustmentDetail(true);
            }
        });
    }

    ngOnDestroy() {
        if (Utils.notNullAndDefined(this.forceReloadSubscription)) {
            this.forceReloadSubscription.unsubscribe();
        }
    }

    onHidden(): void {
        this.closeAPAdjustment(false);
    }

    onTransactionLogClick(): void {
        if (Utils.notNullAndDefined(this.adjustmentDetailParam.id)) {
            this.transactionLogTitle = 'Log of AP Adjustment ' + this.adjustmentDetailParam.id;
            this.populateTransactionLog();
            this.displayTransactionLogPopUp = true;
        }
    }

    onTransactionLogClose(e: boolean): void {
        this.displayTransactionLogPopUp = false;
        if (Utils.isTrue(e)) {
            this.populateAPAdjustmentDetail(true);
        }
    }

    isNotRequiredDimension(dim: any): boolean {
        return Utils.isNullOrEmpty(
            this.adjustmentDetail.requiredDimensions.find((rdim) => rdim.key === dim.dimensionCode)
        );
    }

    onPaymentApplicationClick(data: any): void {
        const param: APCreditApplicationParameterDto = new APCreditApplicationParameterDto();
        param.communityId = this.adjustmentDetailParam.communityId;
        param.managementCompanyId = this.adjustmentDetailParam.managementCompanyId;
        param.creditApplicationId = null;
        if (
            (Utils.equalsIgnoreCase(data.debitOrCredit, ARConstants.ADJUSTMENT_TYPE_DEBIT) && data.amount > 0) ||
            (Utils.equalsIgnoreCase(data.debitOrCredit, ARConstants.ADJUSTMENT_TYPE_CREDIT) && data.amount < 0)
        ) {
            param.sourceId = data.apAdjustmentId;
            param.sourceType = 'A';
        }
        if (
            (Utils.equalsIgnoreCase(data.debitOrCredit, ARConstants.ADJUSTMENT_TYPE_DEBIT) && data.amount < 0) ||
            (Utils.equalsIgnoreCase(data.debitOrCredit, ARConstants.ADJUSTMENT_TYPE_CREDIT) && data.amount > 0)
        ) {
            param.targetId = data.apAdjustmentId;
            param.targetType = 'A';
        }

        param.clearCache = true;

        this.loadingVisible = true;
        this.apCreditApplicationService.getAPCreditApplications(param).subscribe(
            (result: APCreditApplicationResultDto[]) => {
                this.creditApplicationData = result;
                this.creditApplicationTitle = `Payment Application for Adjustment # ${data.apAdjustmentId}`;
                this.showCreditApplication = true;
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.serverErrorService.displayError(error);
            }
        );
    }

    closeCreditApplication(forceRefresh: boolean): void {
        this.showCreditApplication = false;

        if (Utils.isTrue(forceRefresh)) {
            this.populateAPAdjustmentDetail(true);
        }
    }

    onAdditionalInfoClick(data: any): void {
        this.detailPopupsService.showDetailsPopup('AP', data.referenceType, {
            managementCompanyId: data.managementCompanyId,
            communityId: data.communityId,
            headerId: data.referenceId
        });
    }

    calculateCustomSummary(e: any): void {
        this.helperService.calculateTotalSummary(e);
    }

    onEditAdjustmentClick(): void {
        this.showEditAdjustment = true;
        this.apAdjustmentEditor.adjustmentTitle = `Edit AP Adjustment #${this.adjustmentDetail.apAdjustmentId}`;
        this.apAdjustmentEditor.getAPAdjustmentEditor(); // reload the data for the editor after many edits.
    }

    onLimitedEditClick(): void {
        this.isLimitedEdit = true;
        this.onEditAdjustmentClick();
    }

    onEditorClose(forceRefresh: boolean): void {
        this.showEditAdjustment = false;
        this.isLimitedEdit = false;
        if (Utils.isTrue(forceRefresh)) {
            this.populateAPAdjustmentDetail(true);
        }
    }

    onDeleteAdjustment(): void {
        this.deleteAPAdjustment();
    }

    onReverseClick(): void {
        this.getReverseEditor();
    }

    onReverseAdjustment(event: any): void {
        const baseParam: APAdjustmentReverseParameterDto = new APAdjustmentReverseParameterDto();
        baseParam.managementCompanyId = this.adjustmentDetail.managementCompanyId;
        baseParam.communityId = this.adjustmentDetail.communityId;
        baseParam.id = this.adjustmentDetail.apAdjustmentId;
        baseParam.reverseDate = event.reverseDate;
        baseParam.reverseReason = event.reverseReason;

        this.reverseAPAdjustment(baseParam);
    }

    onReverseAdjustmentClose(): void {
        this.showReverseAdjustment = false;
    }

    onApplyDebitClick(): void {
        if (Utils.isTrue(this.adjustmentDetail.showRequiredDimension)) {
            this.showRequiredDimensionSelector = true;
        } else {
            this.showApplyDebitEditor = true;
        }
    }

    onApplyDebitClose(forceRefresh: boolean): void {
        this.showApplyDebitEditor = false;
        this.requiedDimensionOptionCodes = [];
        if (Utils.isTrue(forceRefresh)) {
            this.populateAPAdjustmentDetail(true);
        }
    }

    onVendorNameClick(detail: APAdjustmentResultDto): void {
        if (Utils.notNullAndDefined(detail.vendor.id)) {
            const contextType = 'Community';
            const contextId = detail.communityId;
            const vendorCode = detail.vendor.code;
            const queryString = `contextType=${encodeURIComponent(
                contextType
            )}&contextId=${contextId}&vendorCode=${encodeURIComponent(vendorCode)}`;
            this.helperService.openDefaultVendorManagementPageInNewWindow(queryString);
        }
    }

    onDimensionSelect(dimensionOptions: any): void {
        this.showRequiredDimensionSelector = false;
        if (Utils.notNullOrEmpty(dimensionOptions)) {
            this.requiedDimensionOptionCodes = dimensionOptions;
            this.showApplyDebitEditor = true;
        }
    }

    private populateAPAdjustmentDetail(clearCache: boolean): void {
        this.loadingVisible = true;
        this.adjustmentDetailParam.clearCache = clearCache;
        this.apAdjustmentService.getAPAdjustmentDetail(this.adjustmentDetailParam).subscribe(
            (result: APAdjustmentResultDto) => {
                this.loadingVisible = false;
                result.items.forEach((item) => {
                    item.dimensions.forEach((dim) => {
                        const isRequired = Utils.notNullOrEmpty(
                            result.requiredDimensions.find((rdim) => rdim.key === dim.dimensionCode)
                        );
                        if (Utils.isTrue(isRequired)) {
                            item[dim.dimensionCode] = dim.dimensionOptionTitle;
                        } else {
                            this.hasOtherDimensions = true;
                        }
                    });
                });
                this.adjustmentDetail = result;
            },
            (error) => {
                this.loadingVisible = false;
                this.serverErrorService.displayError(error);
            }
        );
    }

    private populateTransactionLog(): void {
        const baseParam: TransactionLogParameterDto = new TransactionLogParameterDto();
        baseParam.transactionId = this.adjustmentDetailParam.id;
        baseParam.clearCache = true;
        this.loadingVisible = true;
        this.apAdjustmentService.getAPAdjustmentHistory(baseParam).subscribe(
            (result: TransactionLogResultDto[]) => {
                this.transactionLogResultData = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.serverErrorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private deleteAPAdjustment(): void {
        this.loadingVisible = true;
        this.apAdjustmentService.deleteAPAdjustment(this.adjustmentDetailParam).subscribe(
            () => {
                const message = `AP Adjustment #${this.adjustmentDetailParam.id} successfully deleted.`;
                notify(message, 'Success', MessageInterval.successMsgInterval);
                this.closeAPAdjustment(true);
                this.loadingVisible = false;
            },
            (error) => {
                this.serverErrorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private reverseAPAdjustment(baseParams: APAdjustmentReverseParameterDto): void {
        this.loadingVisible = true;
        this.apAdjustmentService.reverseAPAdjustment(baseParams).subscribe(
            () => {
                notify(
                    `AP Adjustment #${this.adjustmentDetail.apAdjustmentId} has been successfully reversed.`,
                    'Success',
                    MessageInterval.successMsgInterval
                );
                this.showReverseAdjustment = false;
                this.loadingVisible = false;
                this.populateAPAdjustmentDetail(true);
            },
            (error) => {
                this.loadingVisible = false;
                this.serverErrorService.displayError(error);
            }
        );
    }

    private closeAPAdjustment(flag: boolean): void {
        this.visible = false;
        this.popUpTitle = '';
        this.adjustmentDetailParam = null;
        this.onAdjustmentDetailClose.emit(flag);
    }

    private getReverseEditor(): void {
        const baseParams: APAdjustmentParameterDto = new APAdjustmentParameterDto();
        baseParams.communityId = this.adjustmentDetail.communityId;
        baseParams.managementCompanyId = this.adjustmentDetail.managementCompanyId;
        baseParams.id = this.adjustmentDetail.apAdjustmentId;

        this.apAdjustmentService.getAdjustmentReverseEditor(baseParams).subscribe(
            (result: ReverseEditorResultDto) => {
                this.reverseEditorData = result;
                this.reverseAPTitle = 'Reverse Adjustment';
                this.showReverseAdjustment = true;
            },
            (error) => {
                this.serverErrorService.displayError(error);
            }
        );
    }
}
